import { EVENT_NAME, trackAnalyticsEvent } from "@leagueplatform/analytics";
import {
  Button,
  GDSButtonProps,
  Icon,
  Modal,
  VisuallyHidden,
} from "@leagueplatform/genesis-core";

type ModalActionButtonProps = {
  actionType: "close" | "submit" | "cancel";
  isMobile?: boolean;
  buttonText?: string;
  trackAnalytics?: {
    product_area?: string;
    screen_name?: string;
    detail?: string;
    // Not used but included if needed in the future
    sub_product_area?: string;
  };
} & Partial<GDSButtonProps<"button">>;

const ConditionalCloseModalWrapper = ({
  shouldClose,
  children,
}: {
  shouldClose: boolean;
  children: JSX.Element;
}) => {
  if (shouldClose) {
    return <Modal.Close>{children}</Modal.Close>;
  }
  return children;
};

export const ModalActionButton: React.FC<ModalActionButtonProps> = ({
  actionType,
  isMobile = false,
  buttonText,
  onClick,
  trackAnalytics,
  ...buttonProps
}: ModalActionButtonProps) => {
  // Determine button settings based on actionType
  const buttonDefaultsMapping: {
    [key: string]: Partial<GDSButtonProps<"button">>;
  } = {
    close: {
      priority: "tertiary",
      icon: "tinyClose",
      size: "small",
    },
    submit: {
      priority: "primary",
      size: isMobile ? "large" : "medium",
      width: isMobile ? "fillContainer" : undefined,
    },
    cancel: {
      priority: "secondary",
      size: isMobile ? "large" : "medium",
      width: isMobile ? "fillContainer" : undefined,
    },
  };

  const buttonDefaults = buttonDefaultsMapping[actionType] || {};

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Track analytics only if trackAnalytics prop is provided
    if (trackAnalytics) {
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: trackAnalytics.product_area,
        screen_name: trackAnalytics.screen_name,
        detail: trackAnalytics.detail,
      });
    }

    // Call original onClick if provided
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <ConditionalCloseModalWrapper
      shouldClose={
        !onClick || actionType === "close" || actionType === "cancel"
      }
    >
      <Button
        type={actionType === "submit" ? "submit" : "button"}
        hideLabel={actionType === "close"}
        onClick={handleClick}
        {...buttonDefaults}
        {...buttonProps}
      >
        {actionType === "close" ? (
          <VisuallyHidden>{buttonText}</VisuallyHidden>
        ) : (
          buttonText
        )}
        {actionType === "close" && (
          <Icon icon={buttonProps.icon as any} size={16} aria-hidden="true" />
        )}
      </Button>
    </ConditionalCloseModalWrapper>
  );
};
